import { api } from '../../lib/api';
import { fmtDate } from '../../lib/utils';
import {
  DEAL_REQUISITI, REQ_STATUS_LABEL, STAGE_OUTCOME_LABEL,
  VOUCHER_CANONE_MENSILE, VOUCHER_CONTRIBUTO, VOUCHER_FEE_ISTRUTTORIA, VOUCHER_SCENARIO_LABEL,
  VOUCHER_SCENARIO_SHORT, VOUCHER_SPESA, VOUCHER_SUCCESS_FEE_RATE, voucherRicavo,
  ModuleKey, PermissionLevel, hasAtLeast,
  type PipelineKind, type ReqStatus, type StageOutcome, type VoucherScenario,
} from '@masamune/shared';

/**
 * Trattative — tipi, query options e helper condivisi tra DealsPage,
 * DealDetailPage e NewDealModal.
 *
 * API: /api/v1/deals (apps/api/src/modules/deals). Gli importi arrivano già
 * come numeri, le date come "YYYY-MM-DD". Query keys: prefisso ['deals', ...].
 *
 * Il modello: ogni trattativa vive in una **pipeline** (voucher, rinnovi,
 * estensioni, nuovi clienti) ed è ferma su uno **step** di quella pipeline.
 * Gli step non sono un enum del codice: arrivano da `/deals/pipelines` e il
 * codice ne guarda solo l'esito (aperto / vinto / perso).
 */

export type ReqStatusKey = ReqStatus;
export type ScenarioKey = VoucherScenario;
export type DealView = 'aperte' | 'vinte' | 'chiuse' | 'tutte';

export const REQUISITI = DEAL_REQUISITI;
export const REQ_LABEL = REQ_STATUS_LABEL;
export const OUTCOME_LABEL = STAGE_OUTCOME_LABEL;
export const SCENARIO_LABEL = VOUCHER_SCENARIO_LABEL;
export const SCENARIO_SHORT = VOUCHER_SCENARIO_SHORT;
export const SCENARI: ScenarioKey[] = ['S20K', 'S40K'];
export const CONTRIBUTO = VOUCHER_CONTRIBUTO;
export const SPESA = VOUCHER_SPESA;
export const FEE_ISTRUTTORIA = VOUCHER_FEE_ISTRUTTORIA;
export const SUCCESS_FEE_RATE = VOUCHER_SUCCESS_FEE_RATE;
export const CANONE_MENSILE = VOUCHER_CANONE_MENSILE;
export const ricavoVoucher = voucherRicavo;

/** Le pill, calcolate sull'esito dello step: nessun nome cablato. */
export const VIEWS: Array<{ key: DealView; label: string }> = [
  { key: 'aperte', label: 'In corso' },
  { key: 'vinte', label: 'Vinte' },
  { key: 'chiuse', label: 'Chiuse' },
  { key: 'tutte', label: 'Tutte' },
];

export interface DealUserLite {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  avatarColor?: string | null;
}

export interface StageLite {
  id: string;
  name: string;
  order: number;
  outcome: StageOutcome;
  probability: number;
  color?: string | null;
}

export interface PipelineLite {
  id: string;
  slug: string;
  name: string;
  kind: PipelineKind;
}

export interface Pipeline extends PipelineLite {
  description?: string | null;
  order: number;
  active: boolean;
  stages: StageLite[];
  dealsCount?: number;
}

export interface DealNextTask {
  id: string;
  title: string;
  dueDate: string | null;
  status: string;
  assignee: DealUserLite | null;
  isOverdue: boolean;
}

/**
 * Il contratto in essere che la trattativa rinnova. Sta qui perché un rinnovo
 * non è denaro nuovo: è denaro che rischiamo di perdere, e il confronto fra
 * quanto vale oggi (`annuo`) e quanto proponiamo dice se stiamo salendo o no.
 */
export interface DealRenewalOf {
  id: string;
  name: string;
  amountNet: number;
  cycle: string;
  annuo: number;
  endDate: string | null;
}

export interface DealContract extends DealRenewalOf {
  client: { id: string; shortName: string; legalName: string } | null;
}

/**
 * Il progetto nato da questa trattativa, se è nato. È una riga sola sulla
 * scheda: "ne è nato il progetto X, a che punto è". Resta null quando la
 * trattativa è vinta ma non ha prodotto una commessa (un canone venduto è una
 * sottoscrizione, non un progetto) — il legame è facoltativo nei due sensi.
 */
export interface DealProjectLink {
  id: string;
  code: string;
  name: string;
  status: string;
  phase: string;
  progress: number;
  deadline: string | null;
}

export interface DealItem {
  id: string;
  title: string;
  pipelineId: string;
  pipeline: PipelineLite;
  stageId: string;
  stage: StageLite;
  clientId: string | null;
  client: { id: string; shortName: string; legalName?: string } | null;
  contactName: string | null;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  ghlContactId: string | null;
  /** true se la pipeline è quella del voucher: solo lì valgono scenario e requisiti. */
  isVoucher: boolean;
  /** Importo libero: vale solo fuori dal voucher. */
  amount: number;
  scenario: ScenarioKey | null;
  mesiCanone: number;
  /** Contributo del bando: metà della spesa col tetto di 20.000. Non è un ricavo. */
  contributo: number | null;
  /** Gestione pratica: istruttoria + success fee + canone. Si vende SOPRA il progetto. */
  feeGestione: number | null;
  /** @deprecated stesso numero di feeGestione, tenuto per le viste vecchie. */
  ricavoStimato: number | null;
  /** Il valore che conta: sempre l'imponibile che fatturiamo noi, voucher compreso. */
  value: number;
  weighted: number;
  probability: number;
  probabilityManual: boolean;
  reqConnettivita: ReqStatusKey;
  reqPolizza: ReqStatusKey;
  reqDurc: ReqStatusKey;
  requisitiMancanti: string[];
  requisitiOk: boolean;
  /** Vinta con 20k: può ancora estendere a 40k entro tre mesi. */
  estendibile: boolean;
  expectedCloseDate: string | null;
  fascia: string | null;
  /** Il contratto che questa trattativa rinnova (null se è denaro nuovo). */
  renewalOf: DealRenewalOf | null;
  isRinnovo: boolean;
  /** Il progetto nato da qui: finché c'è, quel valore non è più potenziale. */
  project: DealProjectLink | null;
  ownerId: string | null;
  owner: DealUserLite | null;
  lostReason: string | null;
  note: string | null;
  order: number;
  lastEventAt: string;
  daysSinceLastEvent: number;
  isOpen: boolean;
  isWon: boolean;
  isClosed: boolean;
  nextTask: DealNextTask | null;
  createdAt?: string;
}

export interface DealEventItem {
  id: string;
  kind: 'STAGE' | 'NOTA' | 'CONTATTO';
  text: string;
  createdAt: string;
  createdBy: DealUserLite | null;
}

export type DealFull = DealItem & {
  /** Gli step della pipeline di questa trattativa, in ordine. */
  stages: StageLite[];
  events: DealEventItem[];
  tasks: DealNextTask[];
};

export interface DealListResponse {
  items: DealItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  totals: { amount: number; weighted: number };
}

export interface DealPeriod {
  key: string;
  label: string;
  from: string;
  to: string;
  count: number;
  amount: number;
  weighted: number;
}

export interface DealSummary {
  today: string;
  pipeline: string | null;
  staleDays: number;
  byPeriod: DealPeriod[];
  undated: { count: number; amount: number; weighted: number };
  /** Voucher senza configurazione scelta: il ricavo non si può ancora calcolare. */
  missingScenario: number;
  /**
   * Quelle già diventate progetto: il loro valore è USCITO dal potenziale
   * (byPeriod, byPipeline) ed è entrato nel progetto. Sta qui solo per poter
   * spiegare perché il potenziale è sceso, non per risommarlo.
   */
  converted: { count: number; amount: number; weighted: number };
  byStage: Array<{ stageId: string; name: string; outcome: StageOutcome; pipeline: string; count: number; amount: number; weighted: number }>;
  byPipeline: Array<{ slug: string; name: string; count: number; amount: number; weighted: number }>;
  stale: number;
}

export interface DealListParams {
  view: DealView;
  pipeline?: string;
  stageId?: string;
  ownerId?: string;
  fascia?: string;
  search?: string;
  from?: string;
  to?: string;
  stale?: boolean;
  /** Ordina per (solo la lista: la bacheca ha il suo ordine, quello del trascinamento). */
  sort?: string;
  /** Filtra per "cosa c'è da sistemare": azione-ritardo, senza-azione, senza-data, senza-valore. */
  solo?: string;
  page?: number;
  pageSize?: number;
}

// ── Query options ──────────────────────────────────────────────────────────

export const pipelinesQuery = {
  queryKey: ['deals', 'pipelines'] as const,
  queryFn: async (): Promise<Pipeline[]> => (await api.get<{ items: Pipeline[] }>('/deals/pipelines')).items ?? [],
  staleTime: 5 * 60_000,
};

export function dealsSummaryQuery(pipeline?: string) {
  return {
    queryKey: ['deals', 'summary', pipeline ?? ''] as const,
    queryFn: () => api.get<DealSummary>(`/deals/summary${pipeline ? `?pipeline=${encodeURIComponent(pipeline)}` : ''}`),
    staleTime: 30_000,
  };
}

export function buildListQuery(p: DealListParams): string {
  const sp = new URLSearchParams();
  sp.set('view', p.view);
  if (p.pipeline) sp.set('pipeline', p.pipeline);
  if (p.stageId) sp.set('stageId', p.stageId);
  if (p.ownerId) sp.set('ownerId', p.ownerId);
  if (p.fascia) sp.set('fascia', p.fascia);
  if (p.search?.trim()) sp.set('search', p.search.trim());
  if (p.from) sp.set('from', p.from);
  if (p.to) sp.set('to', p.to);
  if (p.stale) sp.set('stale', 'true');
  if (p.sort) sp.set('sort', p.sort);
  if (p.solo) sp.set('solo', p.solo);
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return `/deals?${sp.toString()}`;
}

export function dealsListQuery(p: DealListParams) {
  return {
    queryKey: ['deals', 'list', p] as const,
    queryFn: () => api.get<DealListResponse>(buildListQuery(p)),
    staleTime: 15_000,
  };
}

export function dealDetailQuery(id: string) {
  return {
    queryKey: ['deals', 'detail', id] as const,
    queryFn: () => api.get<DealFull>(`/deals/${id}`),
  };
}

export const dealOwnersQuery = {
  queryKey: ['deals', 'owners'] as const,
  queryFn: async (): Promise<DealUserLite[]> => (await api.get<{ items: DealUserLite[] }>('/deals/owners')).items ?? [],
  staleTime: 5 * 60_000,
};

export const dealFasceQuery = {
  queryKey: ['deals', 'fasce'] as const,
  queryFn: async (): Promise<string[]> => (await api.get<{ items: string[] }>('/deals/fasce')).items ?? [],
  staleTime: 5 * 60_000,
};

/** I contratti in essere: la tendina "questa trattativa rinnova…". */
export const dealContractsQuery = {
  queryKey: ['deals', 'contracts'] as const,
  queryFn: async (): Promise<DealContract[]> => (await api.get<{ items: DealContract[] }>('/deals/contracts')).items ?? [],
  staleTime: 5 * 60_000,
};

/**
 * La proposta sul confine fra trattativa e progetto.
 *
 * Il server non decide da solo: dice cosa si può fare adesso e riempie la
 * bozza con quello che la trattativa sa già. Divisione e modello restano
 * vuoti apposta — in fase di vendita non si sanno, e non si indovinano.
 */
export type ProposalAction = 'PROGETTO' | 'RINNOVO' | 'GIA_FATTO' | 'NON_ANCORA';

export interface DealProjectProposal {
  dealId: string;
  dealTitle: string;
  pipeline: { slug: string; name: string; kind: PipelineKind };
  stage: { name: string; outcome: StageOutcome };
  azione: ProposalAction;
  /** La frase da mostrare: spiega perché si propone questo e non altro. */
  motivo: string;
  project: DealProjectLink | null;
  draft: {
    clientId: string | null;
    clientName: string | null;
    /** Senza cliente in anagrafica non si fattura: si vede prima di aprire. */
    clienteMancante: boolean;
    name: string;
    revenue: number;
    deadline: string | null;
    division: string | null;
    model: string | null;
  } | null;
  /** Il canone di gestione pratica che nascerebbe insieme (solo voucher). */
  canone: { amountNet: number; mesi: number; totale: number; inizio: string | null; fine: string | null } | null;
  /** Il contratto da prolungare, quando la vittoria è un rinnovo. */
  contratto: {
    id: string; name: string; amountNet: number; cycle: string;
    annuo: number; startDate: string | null; endDate: string | null;
  } | null;
  /** Quanto valore uscirebbe dal potenziale commerciale. */
  valore: number;
}

export function dealProposalQuery(id: string) {
  return {
    queryKey: ['deals', 'proposal', id] as const,
    queryFn: () => api.get<DealProjectProposal>(`/deals/${id}/project-proposal`),
    staleTime: 15_000,
  };
}

export const dealClientsQuery = {
  queryKey: ['deals', 'clients-lite'] as const,
  queryFn: () => api.get<{ items: Array<{ id: string; shortName: string; legalName: string }> }>('/clients?pageSize=200'),
  staleTime: 5 * 60_000,
};

// ── Permessi ───────────────────────────────────────────────────────────────

export function canWriteDeals(user: { permissions: Array<{ module: string; level: string }> } | null): boolean {
  if (!user) return false;
  const lvl = user.permissions.find((p) => p.module === ModuleKey.TRATTATIVE)?.level ?? PermissionLevel.NONE;
  return hasAtLeast(lvl as PermissionLevel, PermissionLevel.WRITE);
}

// ── Step: il codice guarda l'esito, mai il nome ────────────────────────────

export const pathStages = (p: Pipeline | undefined) => (p?.stages ?? []).filter((s) => s.outcome !== 'PERSO');
export const exitStages = (p: Pipeline | undefined) => (p?.stages ?? []).filter((s) => s.outcome === 'PERSO');

// ── Etichette e date ───────────────────────────────────────────────────────

/** Riga sotto il titolo: persona e cliente, senza ripetere l'azienda. */
export function contactLine(d: Pick<DealItem, 'contactName' | 'client' | 'companyName'>): string {
  const parts: string[] = [];
  if (d.contactName) parts.push(d.contactName);
  if (d.client?.shortName && d.client.shortName !== d.companyName) parts.push(d.client.shortName);
  return parts.join(' · ');
}

/** Chi è la controparte: l'azienda se c'è, altrimenti la persona. */
export function counterparty(d: Pick<DealItem, 'companyName' | 'client' | 'contactName' | 'title'>): string {
  return d.companyName || d.client?.shortName || d.contactName || d.title;
}

const DAY_MS = 86400000;

function todayKey(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Giorni tra oggi e una data YYYY-MM-DD (negativo = passata). */
export function daysFromToday(ymd: string | null | undefined): number | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const [ty, tm, td] = todayKey().split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty!, tm! - 1, td!)) / DAY_MS);
}

export interface DueInfo { label: string; tone: 'alert' | 'warn' | 'mute' }

/** Etichetta di una scadenza: "oggi", "domani", "in ritardo di 3 g", altrimenti la data. */
export function dueInfo(ymd: string | null | undefined, closed = false): DueInfo {
  const days = daysFromToday(ymd);
  if (days == null) return { label: '—', tone: 'mute' };
  if (closed) return { label: fmtDate(ymd), tone: 'mute' };
  if (days < 0) return { label: `in ritardo di ${-days} g`, tone: 'alert' };
  if (days === 0) return { label: 'oggi', tone: 'warn' };
  if (days === 1) return { label: 'domani', tone: 'warn' };
  if (days <= 6) return { label: `tra ${days} g`, tone: 'mute' };
  return { label: fmtDate(ymd), tone: 'mute' };
}

export function fullName(u: DealUserLite | null | undefined): string {
  return u ? `${u.firstName} ${u.lastName}`.trim() : '—';
}

export function shortName(u: DealUserLite | null | undefined): string {
  return u ? `${u.firstName.charAt(0)}. ${u.lastName}` : '—';
}

